/* <product-form>: one form for one product, on the product page, in quick add and in a product row.
   It works without JS (the form posts the variant). Here it resolves the variant from the choices, keeps the
   button's step, price and the low-stock line in step, marks sizes that are sold out with a visible status,
   and adds to the bag in place. It announces every change as «variant:change»
   so the page can follow (the price line, the pictures, the buy bar). What Shopify sells is added, also a product
   that is coming (presale). A variant that cannot be bought says «Gi meg beskjed» once the club takes addresses: the
   button opens the notify view
   (snippets/buy-buttons.liquid) for that variant instead of adding. Fully sold-out products open this panel
   directly and collect any missing size inside the notification form.
   Where the product has selling plans, the price follows «Engangskjøp» or «Abonnement»
   (snippets/purchase-options.liquid) too. A set's parts can share one row of sizes or have a row each. */
const money = (cents) => `${Math.round(cents / 100).toLocaleString('nb-NO')} kr`;
const still = matchMedia('(prefers-reduced-motion: reduce)');
const ONE = /^(default title|onesize)$/i;
/** A value as it reads: a range of numbers with an en dash («36–38», «2–3 år»), as snippets/range.liquid. */
const range = (value) => (/^\d/.test(value) ? value.replaceAll('-', '–') : value);
let cartMutationId = 0;

class ProductForm extends HTMLElement {
  /** Adds a form's variant to the bag, with the views «Lagt til» and the bag need, so nothing is fetched after. */
  static async post(form) {
    const body = new FormData(form);
    body.append('sections', 'added,cart-drawer');
    // Invalidate dependent cart controls before even a programmatic quick add starts.
    const id = `product-form:${++cartMutationId}`;
    document.dispatchEvent(new CustomEvent('cart:pending', { detail: { pending: true, id } }));
    // Offline, or an answer that is not JSON, is a refusal like any other: the caller says it.
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      return { ok: response.ok, item: await response.json() };
    } catch {
      return { ok: false, item: {} };
    } finally {
      document.dispatchEvent(new CustomEvent('cart:pending', { detail: { pending: false, id } }));
    }
  }

  connectedCallback() {
    this.connection = (this.connection || 0) + 1;
    this.events?.abort();
    this.events = new AbortController();
    const { signal } = this.events;
    this.form = this.querySelector('form');
    const json = this.querySelector('[data-product-json]');
    this.data = json ? JSON.parse(json.textContent) : null;
    this.button = this.querySelector('[data-buy]');
    this.notify = this.querySelector('template[data-view="notify"]');
    this.said = this.querySelector('[data-said]');
    this.addEventListener('change', this, { signal });
    this.addEventListener('click', this, { signal });
    // A pick made with a pointer (not arrow keys moving through the sizes) can be the add in quick add.
    this.addEventListener('pointerdown', this, { signal });
    this.addEventListener('keydown', this, { signal });
    // Before the browser validates: without a size the button brings the sizes into view instead.
    this.button?.addEventListener('click', this, { capture: true, signal });
    // Without JS the browser asks for a size itself (the radios are required); the sizes are marked as well.
    this.form.addEventListener('invalid', this, { capture: true, signal });
    this.form.addEventListener('submit', this, { signal });
    this.recipient();
    this.initialPicker();
    this.sync();
  }

  disconnectedCallback() {
    this.connection++;
    this.events?.abort();
  }

  /** «Send direkte til mottakeren» (snippets/gift-recipient.liquid): the recipient's fields post, and ask for the
     address, only while the box is ticked. Without JS they post as they are. */
  recipient() {
    const fields = this.form.querySelector('[data-recipient]');
    if (!fields) return;
    const on = this.form.querySelector('[data-recipient-toggle]').checked;
    fields.disabled = !on;
    fields.querySelector('[type=email]').required = on;
  }

  handleEvent(event) {
    if (event.type === 'pointerdown' || event.type === 'keydown') {
      this.pointed = event.type === 'pointerdown';
      return;
    }
    if (event.type === 'invalid') return event.target.closest('.choice-group')?.setAttribute('data-error', '');
    if (event.type === 'submit') return this.add(event);
    if (event.type === 'change') {
      // The frequency names the plan «Abonnement» posts.
      if (event.target.matches('[data-frequency]')) {
        const subscription = this.form.querySelector('[data-subscription]');
        subscription.value = event.target.value;
      }
      if (event.target.matches('[data-recipient-toggle]')) return this.recipient();
      return this.choose(event.target);
    }
    if (event.currentTarget === this.button && this.button.dataset.mode === 'choose') {
      event.preventDefault();
      return this.pick();
    }
    const toggle = event.target.closest('[data-per-part-toggle]');
    if (toggle) this.perPart(toggle);
  }

  /** The chosen variant's index, or -1 while a choice is missing. */
  get index() {
    if (!this.data) return this.form.querySelector('input[name=id]')?.value ? 0 : -1;
    const single = this.form.querySelector('input[type=radio][name=id]:checked');
    if (single) return this.data.variants.indexOf(Number(single.value));
    const picked = this.picked;
    if (picked.filter((value) => value !== undefined).length < this.data.options[0].length) return -1;
    return this.data.options.findIndex((values) => values.every((value, i) => value === picked[i]));
  }

  /** The chosen selling plan's id, or '' for a one-off purchase. */
  get plan() {
    return this.form.querySelector('input[name=selling_plan]:checked:not(:disabled)')?.value || '';
  }

  /** A variant's price with a plan (Shopify's allocation), or its one-off price. */
  priceOf(index, plan) {
    return (plan ? this.data.plans?.[index]?.[plan] : undefined) ?? this.data.prices[index];
  }

  /** Until every option is chosen, retain the price/allocation of the variant rendered or last chosen. */
  priceIndex(index) {
    if (index >= 0) return index;
    const initial = Number(this.form.querySelector('[data-purchase]')?.dataset.variant);
    return Math.max(this.data?.variants.indexOf(initial) ?? -1, 0);
  }

  /** Keep only this variant's allocated plans selectable; a variant without one falls back to one-off. */
  purchase(index) {
    const subscription = this.form.querySelector('[data-subscription]');
    if (!subscription) return true;
    const frequency = this.form.querySelector('[data-frequency]');
    const onetime = this.form.querySelector('input[name=selling_plan][value=""]:not([data-subscription])');
    const at = this.priceIndex(index);
    const modes = this.form.querySelector('[data-purchase]');
    if (this.data && index >= 0) modes.dataset.variant = this.data.variants[index];
    const options = [...frequency.options];
    if (this.data) {
      const allocations = this.data.plans?.[at] || {};
      options.forEach((option) => {
        const price = allocations[option.value];
        option.disabled = price === undefined || price === null;
        option.hidden = option.disabled;
        if (!option.disabled) {
          option.dataset.price = price;
          option.dataset.perDelivery = this.data.planDelivery?.[at]?.[option.value] === true;
        }
      });
      if (onetime) {
        onetime.dataset.price = this.data.prices[at];
        onetime.dataset.compare = this.data.compare[at] || 0;
      }
    }
    const supported = options.filter((option) => !option.disabled);
    const selected = supported.find((option) => option.value === subscription.value) || supported[0];
    frequency.disabled = !selected;
    frequency.closest('.purchase-frequency').hidden = !selected;
    subscription.disabled = !selected;
    subscription.closest('[data-purchase-subscription]').hidden = !selected;
    subscription.value = selected?.value || '';
    if (selected) {
      frequency.value = selected.value;
      subscription.dataset.price = selected.dataset.price;
      if (!onetime) subscription.checked = true;
    } else if (subscription.checked) {
      subscription.checked = false;
      if (onetime) onetime.checked = true;
    }
    const onePrice = this.data
      ? this.data.prices[at]
      : Number(this.form.querySelector('[data-purchase]').dataset.onetimePrice);
    this.form.querySelector('[data-purchase-price="onetime"]')?.replaceChildren(money(onePrice));
    const price = this.form.querySelector('[data-purchase-price="subscription"]');
    if (price && selected) price.textContent = money(Number(selected.dataset.price));
    const perDelivery = selected?.dataset.perDelivery === 'true';
    const delivery = this.form.querySelector('[data-purchase-delivery]');
    if (delivery) delivery.textContent = perDelivery ? delivery.dataset.deliveryLabel : delivery.dataset.paymentLabel;
    const saving = this.form.querySelector('[data-purchase-saving]');
    if (saving) {
      // The maximum is per separate delivery of this item, including freight once below the one-off threshold.
      // A prepaid payment can cover several deliveries; its payment cannot be compared with one item.
      const discount = selected && perDelivery ? onePrice - Number(selected.dataset.price) : 0;
      const threshold = Number(modes.dataset.freeShippingThreshold || 0);
      const shipping = threshold > 0 && onePrice >= threshold ? 0 : Math.max(0, Number(modes.dataset.shipping || 0));
      const amount = discount > 0 ? discount + shipping : 0;
      saving.hidden = amount <= 0;
      saving.textContent = amount > 0 ? saving.dataset.template.replace('#', money(amount)) : '';
    }
    return !!this.form.querySelector('input[name=selling_plan]:checked:not(:disabled)');
  }

  get picked() {
    return [...this.form.querySelectorAll('[data-option]:checked')].reduce((all, input) => {
      all[input.dataset.option] = input.value;
      return all;
    }, []);
  }

  /** The first row in sight that still lacks a choice (a part's row counts while it is closed). */
  get missing() {
    return [...this.form.querySelectorAll('.choice-group')].find(
      (group) => !group.closest('[hidden]') && !group.querySelector(':checked'),
    );
  }

  /** Start a set on visible garment choices when any common size is unavailable. A linked mixed-size variant
     also shows its individual choices; reconnecting the same picker keeps the customer's chosen view. */
  initialPicker() {
    const shared = this.form.querySelector('[data-shared-row]');
    const parts = this.form.querySelector('[data-per-part]');
    if (!this.data || !shared || !parts || this.initialShared === shared) return;
    this.initialShared = shared;
    const inputs = [...shared.querySelectorAll('[data-shared-value]')];
    const values = this.picked.filter((value) => value !== undefined);
    const complete = values.length === this.data.options[0].length;
    const same = complete && new Set(values).size === 1 ? values[0] : null;
    if ((complete && !same) || inputs.some((input) => !this.whole(input.value)))
      this.perPart(this.form.querySelector('[data-per-part-toggle]'), true);
    else inputs.forEach((input) => (input.checked = input.value === same));
  }

  /** One variant of the whole set has this size in every part, and it can be bought. */
  whole(value) {
    return this.data.options.some((values, i) => this.data.available[i] && values.every((v) => v === value));
  }

  choose(input) {
    // One row for a whole set: the size goes to every part, or, where the set does not have it in every part, to
    // the parts that have it, and the rows per part open on the first without (V3).
    if (input.matches('[data-shared-value]')) {
      if (!this.whole(input.value)) return this.split(input.value);
      this.form.querySelectorAll('[data-option]').forEach((option) => {
        option.checked = option.value === input.value;
      });
    }
    this.sync();
    this.updateAddress();
    // Quick add with one row of sizes: picking the size is the add, as V3.
    if (
      'pickAdds' in this.dataset &&
      this.pointed &&
      input.matches('[data-option], input[name=id], [data-shared-value]') &&
      this.button?.dataset.mode === 'add' &&
      !this.button.disabled
    )
      this.form.requestSubmit();
  }

  split(value) {
    const { data } = this;
    this.form.querySelectorAll('[data-option]').forEach((option) => {
      const at = Number(option.dataset.option);
      option.checked =
        option.value === value && data.options.some((values, i) => data.available[i] && values[at] === value);
    });
    const toggle = this.form.querySelector('[data-per-part-toggle]');
    this.perPart(toggle, true);
    this.sync();
    this.updateAddress();
    this.pick(toggle?.dataset.split);
  }

  /** A shared or reloaded product address keeps the size just chosen. Quick add leaves its host page alone. */
  updateAddress() {
    if (!this.data || !this.closest('[data-product-page]')) return;
    const url = new URL(location.href);
    if (this.index >= 0) url.searchParams.set('variant', this.data.variants[this.index]);
    else url.searchParams.delete('variant');
    if (url.href !== location.href) history.replaceState(history.state, '', url);
  }

  /** The notify view's sign-up is ready when the theme settings hold Klaviyo's public key and list. */
  get canNotify() {
    const signup = this.notify?.content.querySelector('club-signup');
    return !!(signup?.dataset.key && signup.dataset.list);
  }

  /** The choice in words, for the buy bar: «Tights M · Topp M», «S · Short», «Blush · Levering hver 30. dag». */
  summary(index) {
    const { data } = this;
    const values =
      data && index >= 0 && !data.gift
        ? data.options[index]
            .map((value, at) => (ONE.test(value) ? '' : data.set ? `${data.names[at]} ${range(value)}` : range(value)))
            .filter(Boolean)
        : [];
    const plan = this.form.querySelector('input[name=selling_plan]:checked:not(:disabled)');
    if (plan)
      values.push(
        plan.value
          ? this.form.querySelector('[data-frequency]').selectedOptions[0].textContent
          : plan.closest('label').querySelector('.purchase-mode-name').textContent,
      );
    return values.join(' · ');
  }

  sync() {
    const { data, button } = this;
    // A product row's «Legg til» has nothing to follow; one variant follows only for «Gi meg beskjed».
    if (!button || (!data && !this.notify && !this.form.querySelector('[data-purchase]'))) return;
    const index = this.index;
    const purchase = this.purchase(index);
    // Fully sold-out products open the waitlist first; its form collects the exact size before registration.
    const none = 'soldOut' in button.dataset;
    const inStock = !none && (data ? index >= 0 && data.available[index] : true);
    const available = inStock && purchase;
    const notify = this.canNotify && !inStock && (none || index >= 0);
    const choosing = index < 0 && !none;
    const busy = button.hasAttribute('aria-busy');
    const plan = this.plan;
    const comparable = this.form.querySelector('[data-frequency]')?.selectedOptions[0]?.dataset.perDelivery === 'true';
    button.dataset.mode = choosing ? 'choose' : notify ? 'notify' : 'add';
    button.formNoValidate = notify;
    const choice = this.missing?.dataset.choose || button.dataset.labelChoose;
    const label = choosing
      ? none && button.dataset.labelNotifyChoice
        ? button.dataset.labelNotifyChoice.replace('#', choice)
        : choice
      : notify
        ? button.dataset.labelNotify
        : available
          ? plan
            ? button.dataset.labelSubscribe || button.dataset.labelAdd
            : button.dataset.labelAdd
          : button.dataset.labelSoldOut;
    button.querySelector('[data-buy-label]').textContent = busy ? button.dataset.labelBusy || label : label;
    button.disabled = busy || (!choosing && !available && !notify);
    // Restock is a stock alert for this exact choice, separate from a product launch.
    const restock = this.querySelector('[data-restock-status]');
    if (restock) {
      restock.hidden = !(notify || (none && choosing));
      const named = (data && index >= 0 ? data.options[index] : [])
        .filter((value) => !ONE.test(value))
        .map(range)
        .join(' / ');
      restock.textContent = restock.hidden
        ? ''
        : named
          ? restock.dataset.variantSoldOut.replace('#', named)
          : restock.dataset.soldOut;
    }
    let price;
    let compare;
    if (data) {
      // Before a choice the button shows the first variant's price, with the purchase that is chosen.
      const at = this.priceIndex(index);
      price = this.priceOf(at, plan);
      // A one-off item can only be compared with a subscription payment covering one delivery.
      compare = plan ? (comparable ? data.prices[at] : 0) : data.compare[at] || 0;
      const hidden = this.form.querySelector('[data-variant-id]');
      if (hidden) {
        hidden.value = index < 0 ? '' : data.variants[index];
        hidden.disabled = index < 0;
      }
      // «Kun n igjen!» from the theme setting's count down, for variants Shopify tracks and does not oversell.
      const note = this.form.querySelector('[data-stock]');
      const left =
        index >= 0 && data.tracked[index] === 'shopify' && data.policy[index] === 'deny' ? data.stock[index] : null;
      if (note) note.textContent = left > 0 && left <= data.lowStock ? note.dataset.low.replace('#', left) : '';
      this.markSoldOut();
      this.name();
      this.form.querySelector('[data-error]')?.removeAttribute('data-error');
    } else {
      // One variant: its prices are written on the choices, and the page's price follows them.
      const chosen = this.form.querySelector('input[name=selling_plan]:checked:not(:disabled)');
      const modes = this.form.querySelector('[data-purchase]');
      if (!chosen && !modes) return;
      const onetime = this.form.querySelector('input[name=selling_plan][value=""]');
      price = Number(chosen?.dataset.price ?? modes.dataset.onetimePrice);
      compare = Number(chosen?.value ? (comparable ? modes.dataset.onetimePrice : 0) : onetime?.dataset.compare) || 0;
    }
    button.querySelector('[data-buy-price]').textContent = none ? '' : money(price);
    this.state = {
      index: data ? index : 0,
      price,
      compare,
      text: money(price),
      before: money(compare),
      media: data && index >= 0 ? data.media?.[index] : 0,
      summary: this.summary(data ? index : 0),
      label: button.querySelector('[data-buy-label]').textContent,
      mode: button.dataset.mode,
      disabled: button.disabled,
    };
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: this.state }));
  }

  /** With several options a value is sold out when no available variant has it together with the other choices;
     in the one row for a set, when the set has no variant of that size in every part that can be bought. */
  markSoldOut() {
    if (!this.form.querySelector('[data-option]')) return;
    const { data } = this;
    const picked = this.picked;
    const mark = (input, free) => {
      const label = input.nextElementSibling;
      label.querySelector('span').toggleAttribute('data-unavailable', !free);
      const said = label.querySelector('[data-sold-text]');
      if (said)
        said.textContent = free ? '' : `${said.classList.contains('choice-status') ? '' : ' · '}${data.soldOut}`;
    };
    this.form.querySelectorAll('[data-option]').forEach((input) => {
      const at = Number(input.dataset.option);
      const free = data.options.some(
        (values, i) =>
          data.available[i] &&
          values[at] === input.value &&
          values.every((value, j) => j === at || picked[j] === undefined || picked[j] === value),
      );
      mark(input, free);
    });
    this.form.querySelectorAll('[data-shared-value]').forEach((input) => mark(input, this.whole(input.value)));
  }

  /** What a titled row shows as chosen: «Blush» over the pictures, «M» on a part's row («Velg» until then). */
  name() {
    this.form.querySelectorAll('[data-chosen-value]').forEach((slot) => {
      if (slot.hasAttribute('data-part-value')) {
        const groups = [...slot.closest('[data-part-group]').querySelectorAll('.choice-group')];
        const values = groups
          .map((group) => group.querySelector('input:checked'))
          .filter(Boolean)
          .map((input) => input.dataset.label || input.value)
          .filter((value) => !ONE.test(value));
        const missing = groups.find((group) => !group.closest('[hidden]') && !group.querySelector('input:checked'));
        if (values.length && missing) values.push(missing.dataset.choose);
        slot.textContent = values.join(' · ') || slot.dataset.empty || '';
        slot.toggleAttribute('data-complete', !missing && values.length > 0);
        return;
      }
      const input = slot.closest('details, fieldset').querySelector('input:checked');
      slot.textContent = input ? input.dataset.label || input.value : slot.dataset.empty || '';
    });
  }

  /** A choice is missing: its row glides into view, is marked, its first choice gets focus, and a screen reader
     hears what to do (the button's step, or what was said). */
  pick(message) {
    const group = this.missing;
    if (!group) return;
    group.setAttribute('data-error', '');
    const row = group.closest('details');
    if (row) row.open = true;
    // Out of reach: under the header, or under the buy bar's place at the foot.
    const r = group.getBoundingClientRect();
    const root = getComputedStyle(document.documentElement);
    const top = parseFloat(root.getPropertyValue('--lly-header-height'));
    const bottom = parseFloat(root.getPropertyValue('--sticky-height')) || 0;
    if (r.top < top || r.bottom > innerHeight - bottom)
      group.scrollIntoView({ behavior: still.matches ? 'auto' : 'smooth', block: 'center' });
    group.querySelector('input')?.focus({ preventScroll: true });
    if (!this.said) return;
    // Emptied first, so the same words are said again on a second try.
    this.said.textContent = '';
    requestAnimationFrame(() => {
      this.said.textContent = message || group.dataset.choose;
    });
  }

  /** «Velg per plagg» swaps the one row for the set for a row per part; «Samme størrelse» swaps back, keeping the
     size when every part has the same. */
  perPart(toggle, on = !this.form.querySelector('[data-shared-row]')?.hidden) {
    const shared = this.form.querySelector('[data-shared-row]');
    const parts = this.form.querySelector('[data-per-part]');
    if (!shared || !parts) return;
    shared.hidden = on;
    parts.hidden = !on;
    if (toggle) toggle.textContent = on ? toggle.dataset.on : toggle.dataset.off;
    if (on) {
      parts.querySelectorAll('details.part-row').forEach((row) => (row.open = true));
      return;
    }
    const values = this.picked.filter((value) => value !== undefined);
    const same = values.length === this.data.options[0].length && new Set(values).size === 1 ? values[0] : null;
    shared.querySelectorAll('[data-shared-value]').forEach((input) => {
      input.checked = input.value === same;
    });
    if (!same)
      this.form.querySelectorAll('[data-option]').forEach((option) => {
        option.checked = false;
      });
    this.sync();
    this.updateAddress();
  }

  /** Open the stock alert for a chosen variant, or let the all-sold-out form collect its missing size. */
  openNotify(trigger = this.button) {
    const { data, notify } = this;
    const index = this.index;
    const view = notify.content;
    let named = '';
    if (data && index >= 0) {
      const varies = (at) => new Set(data.options.map((values) => values[at])).size > 1;
      named = data.options[index]
        .filter((value, at) => varies(at))
        .map(range)
        .join(' / ');
    }
    const signup = view.querySelector('club-signup');
    const variant = data ? (index >= 0 ? String(data.variants[index]) : '') : this.form.querySelector('input[name=id]').value;
    signup.dataset.variant = variant;
    const picker = signup.querySelector('[data-notify-variant-picker]');
    if (picker) {
      // openPanel clones the template: selected attributes carry the current explicit choice into the clone.
      picker.querySelectorAll('option').forEach((option) => option.toggleAttribute('selected', option.value === variant));
      picker.value = variant;
    }
    view.querySelector('[data-notify-variant]').textContent = !picker && named ? ` · ${named}` : '';
    window.Laley.openPanel(notify, { trigger });
  }

  async add(event) {
    event.preventDefault();
    // One add at a time: a second tap while Shopify adds the first does nothing.
    if (this.button?.hasAttribute('aria-busy')) return;
    const trigger = event.submitter || this.button;
    if (this.button?.dataset.mode === 'notify') return this.openNotify(trigger);
    if (this.index < 0) return this.pick();
    if (!this.purchase(this.index) || this.button?.disabled) return;
    const connection = this.connection;
    const button = this.button;
    // «Legger til» while Shopify adds it, so the click answers at once; the step comes back after, added or not.
    const label = this.button?.querySelector('[data-buy-label]');
    const step = label?.textContent;
    const disabled = button?.disabled;
    if (label && this.button.dataset.labelBusy) label.textContent = this.button.dataset.labelBusy;
    this.button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    this.dispatchEvent(new CustomEvent('product:pending', { bubbles: true }));
    const { ok, item } = await ProductForm.post(this.form);
    button?.removeAttribute('aria-busy');
    if (button) button.disabled = disabled;
    if (label) label.textContent = step;
    if (this.isConnected) this.sync();
    this.dispatchEvent(new CustomEvent('product:pending', { bubbles: true }));
    // A replaced editor section or dismissed quick form must not reopen an old view after the cart accepts it.
    if (!this.isConnected || connection !== this.connection) {
      if (ok) document.dispatchEvent(new CustomEvent('cart:changed'));
      return;
    }
    if (ok) return window.Laley.showAdded(item, trigger);
    // What Shopify says («Utsolgt», too many) on the form's own line; a row has none, so the panel's or the page's
    // line says it. Shopify's description is a sentence, or for a field it rejects (the recipient's address) an
    // object; its message then says it in words. Without an answer, the line's own words.
    const note =
      this.form.querySelector('[data-stock]') ||
      this.closest('dialog, main')?.querySelector('[data-stock], [data-panel-said]');
    if (note)
      note.textContent =
        (typeof item.description === 'string' && item.description) || item.message || note.dataset.failed || '';
    // Told to whoever asked for the add (the buy bar brings this line into view).
    this.dispatchEvent(new CustomEvent('cart:refused', { bubbles: true, detail: { note } }));
  }
}

customElements.define('product-form', ProductForm);
